import { Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';

// TagTail promo page — markets Phil's portable LPR hardware project
// (separate repo: ../portable-lpr). Self-contained on purpose so it's easy to
// take down. To remove it entirely:
//   1. delete this file
//   2. in App.js, delete the TagTail import and the /tagtail <Route>
//   3. in Landing.js, delete the TagTail import, the <TagTailNavLink /> line,
//      and the <TagTailBanner /> section
// (grep "TagTail" to find them all).
//
// Purely static marketing — no network calls besides the site's existing
// contact form at the bottom.

// ── Palette (dark, like the landing page, with TagTail's green) ──────────────
const INK = '#0a0b16';
const TEXT = '#e5e7f0';
const MUTED = '#9aa3b8';
const GREEN = '#4ade80';
const GREEN_DIM = 'rgba(74,222,128,.14)';
const GREEN_BORDER = 'rgba(74,222,128,.35)';
const CARD = 'rgba(255,255,255,0.04)';
const CARD_BORDER = 'rgba(255,255,255,0.09)';
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

// TagTail's logo: a map pin holding a plate (same as the app's own icon).
function Logo({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="1.8" strokeLinejoin="round" aria-hidden>
      <path d="M12 22s7-6.6 7-12.2A7 7 0 0 0 5 9.8C5 15.4 12 22 12 22z" />
      <rect x="8.2" y="7.4" width="7.6" height="4.4" rx="0.9" />
      <path d="M10 8.7v1.8M11.33 8.7v1.8M12.67 8.7v1.8M14 8.7v1.8" strokeWidth="1.1" strokeLinecap="round" />
    </svg>
  );
}

// Feather-style line icons, so the page needs nothing new from MUI.
const ICONS = {
  deploy: <><path d="M5 12l5 5L20 7" /><path d="M12 3v3M3 12h3" /></>,
  battery: <><rect x="2" y="7" width="17" height="10" rx="2" /><path d="M22 11v2" /><path d="M10 9l-2 3h4l-2 3" /></>,
  covert: <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><path d="M1 1l22 22" /></>,
  build: <><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></>,
};

function Icon({ name, size = 22, color = GREEN }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {ICONS[name]}
    </svg>
  );
}

function Check({ children }) {
  return (
    <li style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14.5, color: '#cbd5e1', lineHeight: 1.6, marginBottom: 9 }}>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 3 }} aria-hidden>
        <polyline points="20 6 9 17 4 12" />
      </svg>
      <span>{children}</span>
    </li>
  );
}

function SectionTitle({ eyebrow, title, sub }) {
  return (
    <div style={{ marginBottom: 26 }}>
      <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: GREEN, marginBottom: 8 }}>{eyebrow}</div>
      <h2 style={{ fontSize: 'clamp(24px, 3.6vw, 32px)', fontWeight: 800, letterSpacing: '-0.5px', margin: 0, color: '#fff' }}>{title}</h2>
      {sub && <p style={{ fontSize: 15.5, color: MUTED, lineHeight: 1.65, margin: '10px 0 0', maxWidth: 680 }}>{sub}</p>}
    </div>
  );
}

const PILLARS = [
  {
    icon: 'deploy',
    title: 'Deploys in minutes',
    body: 'Plug it in and it starts reading — no keyboard, mouse, or screen. It joins your phone’s hotspot or home Wi-Fi by itself, pairs with your server by pasting one code, and you can just cut the power when you’re done. Nothing is lost.',
  },
  {
    icon: 'battery',
    title: 'Battery powered',
    body: 'The whole kit runs on 5 volts: a portable battery pack or the vehicle’s 12 V socket. No hard-wiring, no installer, nothing left behind when you move it to the next car.',
  },
  {
    icon: 'covert',
    title: 'Covert by design',
    body: 'A camera the size of a matchbox sits behind the windshield in a dark printed hood. No screen in the car, no light bar, no siren-style alerts — hotlist hits show up silently on your phone.',
  },
  {
    icon: 'build',
    title: 'Build it with your own hardware',
    body: 'Off-the-shelf parts you can order today, for about $420. The camera hood is a 3D-print file you can adjust. The server runs on your own computer, so the reads stay yours.',
  },
];


const STEPS = [
  ['Build', 'Order the parts, print the camera hood, and flash the SD card. The installer handles the software, models, Wi-Fi, and start-up service.'],
  ['Pair', 'Add the reader on your TagTail server and paste its connection code once. From then on it pulls models and the hotlist, and pushes every read back.'],
  ['Drive', 'Mount the camera, power it up, and go. Open its page on your phone for the live view and alerts. Reads queue up offline and upload when it’s back in range.'],
];

function Phone() {
  // An illustrative "live read", not real data.
  return (
    <div aria-label="Illustration of a TagTail read on a phone" style={{
      width: '100%', maxWidth: 300, margin: '0 auto', borderRadius: 30, padding: 10,
      background: 'linear-gradient(160deg, #1f2937, #0b0f19)', border: `1px solid ${CARD_BORDER}`,
      boxShadow: '0 30px 80px rgba(0,0,0,.55), 0 0 60px rgba(74,222,128,.10)',
    }}>
      <div style={{ borderRadius: 22, background: '#05070d', padding: '16px 14px 18px', border: '1px solid rgba(255,255,255,.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, fontWeight: 700, color: TEXT }}>
            <Logo size={16} /> TagTail
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: GREEN }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: GREEN, boxShadow: `0 0 8px ${GREEN}` }} />
            Reading · Parked cars
          </span>
        </div>

        {/* Hotlist hit */}
        <div style={{ borderRadius: 12, background: 'rgba(239,68,68,.16)', border: '1px solid rgba(239,68,68,.45)', padding: '10px 12px', marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.08em', color: '#fca5a5', marginBottom: 6 }}>HOTLIST HIT</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 800, fontSize: 18, letterSpacing: '.08em',
              color: '#0f172a', background: '#f8fafc', borderRadius: 5, padding: '3px 9px', border: '2px solid #1e3a8a',
            }}>KJR 4821</span>
            <span style={{ fontSize: 11, color: '#fecaca', lineHeight: 1.4 }}>Gray sedan<br />NY · 98% sure</span>
          </div>
        </div>

        {['LMN 2210', 'HDX 7735', 'T47 9QE'].map((p, i) => (
          <div key={p} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 4px', borderTop: i ? '1px solid rgba(255,255,255,.06)' : 'none',
          }}>
            <span style={{ fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13.5, fontWeight: 700, color: TEXT, letterSpacing: '.05em' }}>{p}</span>
            <span style={{ fontSize: 10.5, color: MUTED }}>{['0:12 ago', '0:41 ago', '1:05 ago'][i]}</span>
          </div>
        ))}

        <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
          {['GPS fix', 'Server ✓', '1 queued'].map(t => (
            <span key={t} style={{ flex: 1, textAlign: 'center', fontSize: 10, color: MUTED, background: CARD, border: `1px solid ${CARD_BORDER}`, borderRadius: 8, padding: '5px 0' }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Illustrative fleet roster, modeled on the server's Edge servers tab. Not real data.
const FLEET = [
  { name: 'Unit 12', online: true, plates: 1284, queued: 0, temp: 142 },
  { name: 'Unit 14', online: true, plates: 967, queued: 3, temp: 151 },
  { name: 'Unit 21', online: true, plates: 1530, queued: 0, temp: 138 },
  { name: 'Unit 7', online: false, plates: 412, queued: 58, temp: null },
];

function FleetPanel() {
  return (
    <div aria-label="Illustration of the fleet view on a TagTail server" style={{
      background: CARD, border: `1px solid ${CARD_BORDER}`, borderRadius: 16, padding: '18px 18px 8px',
      boxShadow: '0 20px 60px rgba(0,0,0,.35)', minWidth: 0,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: '#fff' }}>Edge readers</span>
        <span style={{ fontSize: 11.5, color: GREEN }}>3 of 4 connected · 4,193 plates today</span>
      </div>
      {FLEET.map(u => (
        <div key={u.name} style={{
          display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center',
          padding: '11px 0', borderTop: `1px solid ${CARD_BORDER}`,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: u.online ? GREEN : '#64748b',
                boxShadow: u.online ? `0 0 8px ${GREEN}` : 'none' }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: TEXT }}>{u.name}</span>
              <span style={{ fontSize: 11.5, color: MUTED }}>{u.online ? 'Reading' : 'Offline · last seen 2 h ago'}</span>
            </div>
            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 3, paddingLeft: 16 }}>
              {u.queued ? `${u.queued} waiting to upload` : 'All uploaded'}{u.temp ? ` · ${u.temp} °F` : ''} · current model
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: TEXT, fontVariantNumeric: 'tabular-nums' }}>{u.plates.toLocaleString()}</div>
            <div style={{ fontSize: 10.5, color: MUTED }}>today</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TagTail() {
  return (
    <div style={{ minHeight: '100vh', background: INK, color: TEXT, fontFamily: FONT, overflowX: 'hidden' }}>
      <style>{`
        .tt-wrap { max-width: 1100px; margin: 0 auto; padding-left: 32px; padding-right: 32px; }
        .tt-hero { display: grid; grid-template-columns: 1.15fr .85fr; gap: 48px; align-items: center; }
        .tt-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 16px; }
        .tt-pillars { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        @media (max-width: 1000px) { .tt-pillars { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 560px) { .tt-pillars { grid-template-columns: 1fr; } }
        .tt-two { display: grid; grid-template-columns: 1fr 1fr; gap: 40px; align-items: start; }
        .tt-cta:hover { filter: brightness(1.08); }
        @media (max-width: 860px) {
          .tt-hero, .tt-two { grid-template-columns: 1fr; gap: 36px; }
        }
        @media (max-width: 520px) {
          .tt-wrap { padding-left: 16px; padding-right: 16px; }
        }
      `}</style>

      {/* Nav */}
      <div style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
        <nav className="tt-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 18, paddingBottom: 18 }}>
          <Link to="/" style={{ textDecoration: 'none', fontWeight: 800, fontSize: 15, color: TEXT }}>LE Tech Tools</Link>
          <Link to="/tools" style={{ color: GREEN, textDecoration: 'none', fontSize: 14, fontWeight: 600 }}>All tools →</Link>
        </nav>
      </div>

      {/* Hero */}
      <header style={{ position: 'relative' }}>
        <div aria-hidden style={{ position: 'absolute', top: -80, left: '8%', width: 360, height: 360, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(74,222,128,.18), transparent 65%)', filter: 'blur(24px)', pointerEvents: 'none' }} />
        <div className="tt-wrap tt-hero" style={{ position: 'relative', paddingTop: 56, paddingBottom: 72 }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 22 }}>
              <Logo size={34} />
              <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.3px', color: '#fff' }}>TagTail</span>
              <span style={{
                fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase',
                background: GREEN_DIM, color: GREEN, padding: '3px 10px', borderRadius: 20, border: `1px solid ${GREEN_BORDER}`,
              }}>Hardware project</span>
            </div>
            <h1 style={{ fontSize: 'clamp(34px, 5.4vw, 56px)', fontWeight: 800, letterSpacing: '-1px', lineHeight: 1.05, margin: '0 0 18px', color: '#fff' }}>
              Portable. Cheap.<br /><span style={{ color: GREEN }}>Unseen.</span>
            </h1>
            <p style={{ fontSize: 18, color: MUTED, lineHeight: 1.65, margin: '0 0 28px', maxWidth: 560 }}>
              A license plate reader that fits in a glovebox, runs on a battery, and goes up in any car in minutes.
              Built from parts you can order today — and your reads stay on <strong style={{ color: TEXT }}>your</strong> server.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <a href="#tt-contact" className="tt-cta" style={{
                textDecoration: 'none', padding: '13px 26px', borderRadius: 12, fontSize: 15, fontWeight: 700,
                color: '#052e16', background: GREEN, boxShadow: '0 8px 30px rgba(74,222,128,.30)',
              }}>
                I’m interested
              </a>
              <a href="#tt-cost" style={{
                textDecoration: 'none', padding: '13px 22px', borderRadius: 12, fontSize: 15, fontWeight: 700,
                color: TEXT, border: `1px solid ${CARD_BORDER}`, background: CARD,
              }}>
                What it costs
              </a>
            </div>
          </div>
          <Phone />
        </div>
      </header>

      {/* Four pillars */}
      <section className="tt-wrap" style={{ paddingBottom: 72 }}>
        <SectionTitle eyebrow="Why TagTail" title="Built to go where fixed cameras can’t" />
        <div className="tt-pillars">
          {PILLARS.map(p => (
            <div key={p.title} style={{ background: CARD, border: `1px solid ${CARD_BORDER}`, borderRadius: 16, padding: '24px 22px' }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: GREEN_DIM, border: `1px solid ${GREEN_BORDER}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
                <Icon name={p.icon} />
              </div>
              <h3 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 8px', color: '#fff' }}>{p.title}</h3>
              <p style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.65, margin: 0 }}>{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section style={{ background: 'rgba(255,255,255,.02)', borderTop: `1px solid ${CARD_BORDER}`, borderBottom: `1px solid ${CARD_BORDER}` }}>
        <div className="tt-wrap" style={{ paddingTop: 64, paddingBottom: 64 }}>
          <SectionTitle eyebrow="How it works" title="From parts box to patrol" />
          <div className="tt-grid">
            {STEPS.map(([title, body], i) => (
              <div key={title} style={{ display: 'flex', gap: 16 }}>
                <span style={{
                  flexShrink: 0, width: 34, height: 34, borderRadius: '50%', background: GREEN, color: '#052e16',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15,
                }}>{i + 1}</span>
                <div>
                  <h3 style={{ fontSize: 16.5, fontWeight: 700, margin: '4px 0 6px', color: '#fff' }}>{title}</h3>
                  <p style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.65, margin: 0 }}>{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet */}
      <section className="tt-wrap" style={{ paddingTop: 72 }}>
        <div className="tt-two" style={{ alignItems: 'center' }}>
          <div>
            <SectionTitle
              eyebrow="Scale up"
              title="One reader or a whole fleet"
              sub="Every reader reports to the same server, so adding a car takes a few minutes and one code. Build ten and set them all up the same way."
            />
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <Check><strong style={{ color: TEXT }}>Add a reader with one code.</strong> Name it on the server, paste its connection code once, and it’s part of the fleet.</Check>
              <Check><strong style={{ color: TEXT }}>Set it once, every car follows.</strong> Readers pick up the server’s plate models, settings, and hotlist on their own, so a retrained model or new hotlist entry reaches the whole fleet.</Check>
              <Check><strong style={{ color: TEXT }}>See every car at a glance.</strong> Which readers are online, plates today, uploads waiting, camera, temperature, and power, plus a live map of each car’s position and trail.</Check>
              <Check><strong style={{ color: TEXT }}>Manage from your desk.</strong> Add a car’s Wi-Fi networks from the server, and cut off a lost reader with one click. Its plates stay with you.</Check>
              <Check><strong style={{ color: TEXT }}>Every read in one place.</strong> Search and map across all cars together, or find vehicles seen by different readers in different places.</Check>
            </ul>
          </div>
          <FleetPanel />
        </div>
      </section>

      {/* Capabilities */}
      <section className="tt-wrap" style={{ paddingTop: 72, paddingBottom: 72 }}>
        <div className="tt-two">
          <div>
            <SectionTitle eyebrow="In the car" title="Reads in every situation" />
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <Check><strong style={{ color: TEXT }}>Three modes</strong> — parked cars while you drive, passing cars while you sit, or both moving — with an Auto mode that works out which from the video.</Check>
              <Check><strong style={{ color: TEXT }}>Reads each plate many times and votes</strong> on every character, so one blurry frame doesn’t become a wrong plate.</Check>
              <Check><strong style={{ color: TEXT }}>Hotlist alerts</strong> that tolerate common misreads (O/0, I/1, B/8) and work with or without a connection.</Check>
              <Check><strong style={{ color: TEXT }}>GPS-tagged reads</strong> with time, place, direction, and the reader’s own speed.</Check>
              <Check><strong style={{ color: TEXT }}>Vehicle color, make, and body type</strong>, plus a Save Clip button for the 30 seconds around something worth a second look.</Check>
            </ul>
          </div>
          <div>
            <SectionTitle eyebrow="Back at the desk" title="Search everything it saw" />
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <Check><strong style={{ color: TEXT }}>Search with wildcards and close matches</strong> by date, time of day, color, make, state, and reader. Export to CSV.</Check>
              <Check><strong style={{ color: TEXT }}>Draw on a map</strong> to see every read inside an area.</Check>
              <Check><strong style={{ color: TEXT }}>Plates seen in several places</strong> — draw two to six areas, each with its own time window, and get the vehicles seen in all of them.</Check>
              <Check><strong style={{ color: TEXT }}>Gets smarter on your roads</strong> — confirm or correct reads and retrain the plate reader on your own footage, including which state a plate is from.</Check>
              <Check><strong style={{ color: TEXT }}>Self-hosted</strong> — one server on your own computer collects the reads from every car. No vendor cloud, no subscription.</Check>
            </ul>
          </div>
        </div>
      </section>

      {/* Cost */}
      <section id="tt-cost" style={{ background: 'rgba(255,255,255,.02)', borderTop: `1px solid ${CARD_BORDER}`, borderBottom: `1px solid ${CARD_BORDER}` }}>
        <div className="tt-wrap" style={{ paddingTop: 64, paddingBottom: 64 }}>
          <SectionTitle
            eyebrow="Build your own"
            title="About $420 in parts"
            sub="Everything is off the shelf, and you can swap in better parts as you go. Price is approximate (October 2026)."
          />
          <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.65, margin: 0 }}>
            Plus a 3D-printed camera hood: a rigid sleeve and a flexible skirt that seals against the glass to kill
            windshield reflections — and keeps the camera from catching anyone’s eye.
          </p>
        </div>
      </section>

      {/* Contact */}
      <div id="tt-contact" style={{ paddingTop: 40, paddingBottom: 40 }}>
        <p style={{ textAlign: 'center', fontSize: 13, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: GREEN, margin: '0 0 -10px' }}>
          Interested in TagTail?
        </p>
        <ContactForm />
      </div>

      <footer style={{ borderTop: `1px solid ${CARD_BORDER}` }}>
        <div className="tt-wrap" style={{ paddingTop: 22, paddingBottom: 28, fontSize: 12.5, color: '#6b7280', lineHeight: 1.6 }}>
          TagTail is a separate project from the browser tools on this site: it records plates with your own camera and
          stores them on your own server. Check your agency’s policies and local law before deploying a plate reader.
        </div>
      </footer>
    </div>
  );
}

export default TagTail;

// Nav link for the landing page, next to Guides.
export function TagTailNavLink() {
  return (
    <Link to="/tagtail" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, textDecoration: 'none', color: GREEN, fontSize: 14, fontWeight: 600 }}>
      <Logo size={16} /> TagTail
    </Link>
  );
}

// Promo banner for the landing page. Lives here (not in Home's SECTIONS) so it
// doesn't count as a tool, and so removing this file removes it too.
export function TagTailBanner() {
  return (
    <Link to="/tagtail" style={{
      display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', textDecoration: 'none',
      background: `linear-gradient(135deg, ${INK}, #0f1a14)`, border: `1px solid ${GREEN_BORDER}`,
      borderRadius: 14, padding: '18px 22px', fontFamily: FONT,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <span style={{
        flexShrink: 0, width: 48, height: 48, borderRadius: 12, background: GREEN_DIM, border: `1px solid ${GREEN_BORDER}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Logo size={30} />
      </span>
      <span style={{ flex: '1 1 280px', minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 16, color: '#fff' }}>TagTail</strong>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase',
            background: GREEN_DIM, color: GREEN, padding: '2px 8px', borderRadius: 20, border: `1px solid ${GREEN_BORDER}`,
          }}>New · Hardware</span>
        </span>
        <span style={{ display: 'block', fontSize: 13.5, color: MUTED, lineHeight: 1.55 }}>
          A portable, battery-powered, covert license plate reader you build yourself for about $420.
        </span>
      </span>
      <span style={{ fontSize: 13.5, fontWeight: 700, color: GREEN, whiteSpace: 'nowrap' }}>Learn more →</span>
    </Link>
  );
}
